import { Checkbox, TextField } from '@mui/material';
import { deCamelize } from '../../utils/grid';
import { GridCell } from './GridCell';
import type { IGridEditCellContext, TPhoenixCellContext, TPhoenixColumnDef } from './utility/types';
import type { MouseEvent, ReactNode } from 'react';


export const buildEditCellContext = (ctx: TPhoenixCellContext): IGridEditCellContext => {
  const field = ctx.column.id;
  const meta = ctx.table.options.meta!;
  const row = { ...ctx.row.original, ...(meta.rowDraft ?? {}) };

  return {
    field,
    row,
    value: row[field],
    error: meta.errors?.[field],
    onChange: (value: any) => meta.setDraftValue(field, value),
  };
};
interface ILegacyColOptions {
  headerName?: string;
  description?: string;
  type?: 'string' | 'number' | 'date' | 'dateTime' | 'boolean';
  minWidth?: number;
  maxWidth?: number;
  flex?: number;
  editable?: boolean;
  required?: boolean;
  textWrap?: boolean;
  hide?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  valueFormatter?: (params: { value: any }) => any;
  renderCell?: (params: { value: any; row: any; id: any; formattedValue: any }) => ReactNode;
  renderEditCell?: (ctx: IGridEditCellContext) => ReactNode;
  meta?: Record<string, any>;
  headerAlign?: 'left' | 'center' | 'right';
  align?: 'left' | 'center' | 'right';
}

type TDataGridRowOptions = ILegacyColOptions;
export const isEditingRow = (ctx: TPhoenixCellContext) => ctx.table.options.meta?.editableRowId === ctx.row.id;

export const renderBooleanCell = (ctx: TPhoenixCellContext) => {
  const editing = isEditingRow(ctx);
  const colMeta = ctx.column.columnDef.meta;

  if (editing && colMeta?.editable) {
    const editCtx = buildEditCellContext(ctx);
    return (
      <Checkbox
        checked={!!editCtx.value}
        onClick={(event: MouseEvent) => event.stopPropagation()}
        onChange={(event) => editCtx.onChange(event.target.checked)}
      />
    );
  }

  return ctx.getValue() ? 'Yes' : 'No';
};


export const dataGridCol = (field: string, options?: TDataGridRowOptions): TPhoenixColumnDef => {
  const {
    headerName: rawHeaderName,
    description,
    type,
    minWidth,
    maxWidth,
    flex,
    editable,
    required,
    textWrap,
    hide,
    sortable,
    filterable,
    valueFormatter,
    renderCell,
    renderEditCell,
    meta,
    ...rest
  } = options ?? {};

  const headerName = rawHeaderName ?? deCamelize(field);

  return {
    id: field,
    accessorKey: field,
    header: headerName,
    minSize: minWidth ?? 55,
    maxSize: maxWidth ?? 1000,
    enableSorting: sortable ?? true,
    enableColumnFilter: filterable ?? true,
    cell: (ctx) => {
      const editing = isEditingRow(ctx);
      const colMeta = ctx.column.columnDef.meta;

      if (editing && colMeta?.editable) {
        if (colMeta.renderEditCell) {
          return colMeta.renderEditCell(buildEditCellContext(ctx));
        }
        return <GridDefaultEditCell ctx={buildEditCellContext(ctx)} />;
      }

      const rawValue = ctx.getValue();
      const formatted = colMeta?.valueFormatter ? colMeta.valueFormatter({ value: rawValue, row: ctx.row.original }) : rawValue;

      if (renderCell) {
        return renderCell({ value: rawValue, row: ctx.row.original, id: ctx.row.id, formattedValue: formatted });
      }

      return textWrap ? formatted ?? '' : <GridCell text={formatted ?? ''} />;
    },
    meta: {
      headerName,
      description: description ?? headerName,
      type,
      editable,
      required,
      textWrap,
      hide,
      flex,
      valueFormatter,
      renderEditCell,
      ...meta,
    },
    ...rest,
  };
};
// eslint-disable-next-line react-refresh/only-export-components
const GridDefaultEditCell = ({ ctx }: { ctx: IGridEditCellContext }) => (
  <TextField
    size="small"
    variant="standard"
    fullWidth
    value={ctx.value ?? ''}
    error={!!ctx.error}
    helperText={ctx.error}
    onClick={(event: MouseEvent) => event.stopPropagation()}
    onChange={(event) => ctx.onChange(event.target.value)}
  />
);
